import JSZip from 'jszip';
import { Product } from '../types/product';
import { generateCSV } from './csv/generator';
import { getSequentialFilename } from './imageUtils';
import { getItemNumberDisplay } from './titleProcessor';

interface CompressionOptions {
  watermark?: boolean;
  watermarkSize?: number;
  quality?: number;
  squareOutput?: boolean;
  brightness?: number;
}

export async function compressImage(
  imageData: string,
  watermarkText?: string,
  watermarkSize: number = 50, // Changed from 100 to 50
  quality: number = 0.2, // Changed from 0.7 to 0.2
  maxWidth: number = 1200,
  squareOutput: boolean = true, // Changed default to true
  brightness: number = 1.0 // Default brightness (1.0 = 100%)
): Promise<string> {
  // If quality is 100% and no watermark and no square output and no brightness adjustment, return original image
  if (quality === 1 && !watermarkText && !squareOutput && brightness === 1.0) {
    return imageData;
  }

  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Handle square output if enabled
          if (squareOutput) {
            // Make the canvas square using the smaller dimension instead of larger
            const size = Math.min(width, height);
            canvas.width = size;
            canvas.height = size;
          } else {
            // Calculate new dimensions if width exceeds maxWidth and quality is not 100%
            if (width > maxWidth && quality < 1) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
            canvas.width = width;
            canvas.height = height;
          }

          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Use better quality settings
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw image with white background to handle transparency
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          if (squareOutput) {
            // Center the image in the square canvas
            // Calculate the source coordinates to crop from the center
            const size = Math.min(img.width, img.height);
            const sourceX = (img.width - size) / 2;
            const sourceY = (img.height - size) / 2;
            
            // Apply brightness adjustment
            if (brightness !== 1.0) {
              ctx.filter = `brightness(${brightness})`;
            }
            
            // Draw only the center square portion of the image
            ctx.drawImage(
              img, 
              sourceX, sourceY, size, size, // Source coordinates and dimensions
              0, 0, canvas.width, canvas.height // Destination coordinates and dimensions
            );
          } else {
            // Apply brightness adjustment
            if (brightness !== 1.0) {
              ctx.filter = `brightness(${brightness})`;
            }
            
            ctx.drawImage(img, 0, 0, width, height);
          }
          
          // Reset filter after drawing the image
          ctx.filter = 'none';

          // Add watermark if text is provided
          if (watermarkText) {
            const baseSize = Math.min(canvas.width, canvas.height) * 0.05; // Base size is 5% of the smaller dimension
            const fontSize = Math.round(baseSize * (watermarkSize / 100));
            ctx.font = `bold ${fontSize}px Arial`;
            
            // Create white outline effect for better visibility
            ctx.strokeStyle = 'white';
            ctx.lineWidth = fontSize * 0.15; // Outline thickness proportional to font size
            ctx.lineJoin = 'round';
            
            // Position text in bottom left corner with minimal padding
            const padding = 3; // 3px padding from edges
            const metrics = ctx.measureText(watermarkText);
            const textHeight = fontSize; // Approximate height based on font size
            const x = padding;
            const y = canvas.height - padding - (textHeight * 0.2); // Adjust Y to account for font baseline
            
            // Draw text outline
            ctx.strokeText(watermarkText, x, y);
            
            // Draw text fill
            ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'; // More opaque for better visibility
            ctx.fillText(watermarkText, x, y);
          }

          // Convert to JPEG with specified quality (or PNG if quality is 100%)
          const compressedData = quality === 1 
            ? canvas.toDataURL('image/png') // Use PNG for 100% quality
            : canvas.toDataURL('image/jpeg', quality);
          resolve(compressedData);
        } catch (error) {
          reject(new Error('Failed to process image in canvas'));
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));

      // Handle both data URLs and regular URLs
      if (imageData.startsWith('data:')) {
        img.src = imageData;
      } else {
        // For regular URLs, load as blob first to handle CORS
        fetch(imageData)
          .then(response => response.blob())
          .then(blob => {
            img.src = URL.createObjectURL(blob);
          })
          .catch(() => {
            // Fallback to direct loading if fetch fails
            img.src = imageData;
          });
      }
    } catch (error) {
      reject(new Error('Failed to initialize image compression'));
    }
  });
}

export async function createImageZip(
  images: string[],
  products: Product[],
  options: CompressionOptions = {},
  onProgress?: (current: number) => void
): Promise<Blob> {
  const zip = new JSZip();
  const folder = zip.folder('yahoo_auction_data');
  
  if (!folder) {
    throw new Error('Failed to create ZIP folder');
  }

  let currentImageNumber = 1;
  const failedImages: number[] = [];

  // Process images sequentially to maintain order
  for (let i = 0; i < images.length; i++) {
    try {
      const imageData = images[i];
      // Find the product that owns this image
      const product = products.find(p => p.images.includes(imageData));
      let watermarkText = '';
      
      if (options.watermark && product) {
        const itemNumbers = getItemNumberDisplay(product);
        watermarkText = itemNumbers.join(' ');
      }

      const compressedImage = await compressImage(
        imageData,
        options.watermark ? watermarkText : undefined,
        options.watermarkSize,
        options.quality || 0.2, // Use provided quality or default to 0.2
        1920, // Max width
        options.squareOutput, // Pass square output option
        options.brightness || 1.0 // Pass brightness option
      );
      const base64Data = compressedImage.split(',')[1];
      const filename = getSequentialFilename(currentImageNumber++);
      folder.file(filename, base64Data, { base64: true });

      // Update progress
      if (onProgress) {
        onProgress(i + 1);
      }
    } catch (error) {
      console.error(`Failed to compress image ${i + 1}:`, error);
      failedImages.push(i + 1);
    }
  }

  // Add CSV file
  const csvContent = generateCSV(products, {}, true); // Default to using description templates
  folder.file('products.csv', '\uFEFF' + csvContent, { binary: false });

  // Add compression report if there were failures
  if (failedImages.length > 0) {
    const report = `Failed to compress the following images:\n${failedImages.join(', ')}`;
    folder.file('compression_report.txt', report);
  }

  return zip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: {
      level: 9 // Increased from 6 to 9 for maximum compression
    }
  });
}

export function downloadZip(blob: Blob, filename: string = 'yahoo_auction.zip'): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}